import * as XLSX from 'xlsx';
import {
  ASSIGNER_BULK_MAX_ROWS,
  AssignerBulkEntityType,
  AssignerBulkLookupMaps,
  AssignerBulkLookupOption,
  AssignerBulkParseResult,
  AssignerBulkPreviewRow,
  AssignerBulkTemplateFormat,
  PRODUCT_TEMPLATE_HEADERS,
  RECIPE_TEMPLATE_HEADERS,
} from './assigner-bulk-upload.models';

function normalizeHeader(header: string): string {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
}

function cell(row: Record<string, string>, ...aliases: string[]): string {
  for (const alias of aliases) {
    const key = normalizeHeader(alias);
    const match = Object.keys(row).find((k) => normalizeHeader(k) === key);
    if (match != null && row[match] != null) {
      return String(row[match]).trim();
    }
  }
  return '';
}

/** Minimal RFC4180-ish CSV parse (handles quotes and commas). */
export function parseCsvText(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const normalized = String(text || '').replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    if (ch === '"') {
      // Keep quote chars in the line so splitCsvLine can honour quoted commas.
      // (Previously quotes were stripped here, which broke fields like Professional Notes.)
      current += '"';
      if (inQuotes && normalized[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === '\n' && !inQuotes) {
      lines.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.length || lines.length === 0) {
    lines.push(current);
  }

  const nonEmpty = lines.filter((l, idx) => idx === 0 || l.trim().length > 0);
  if (nonEmpty.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = splitCsvLine(nonEmpty[0]).map((h) => h.trim());
  const rows = nonEmpty.slice(1).map((line) => {
    const values = splitCsvLine(line);
    const record: Record<string, string> = {};
    headers.forEach((header, i) => {
      if (!header) return;
      record[header] = (values[i] ?? '').trim();
    });
    return record;
  });

  return { headers, rows };
}

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  result.push(current);
  return result;
}

export function downloadAssignerBulkTemplate(
  entityType: AssignerBulkEntityType,
  format: AssignerBulkTemplateFormat = 'xlsx'
): void {
  const headers =
    entityType === 'recipe' ? RECIPE_TEMPLATE_HEADERS : PRODUCT_TEMPLATE_HEADERS;
  const baseName =
    entityType === 'recipe'
      ? 'Assigner_Recipe_Template'
      : entityType === 'finishProduct'
        ? 'Assigner_FinishProduct_Template'
        : 'Assigner_Supplement_Template';

  if (format === 'xlsx') {
    const sheet = XLSX.utils.aoa_to_sheet([headers]);
    sheet['!cols'] = headers.map((h) => ({ wch: Math.min(36, Math.max(16, h.length + 4)) }));
    const book = XLSX.utils.book_new();
    const sheetName = entityType === 'recipe' ? 'Recipes' : 'Products';
    XLSX.utils.book_append_sheet(book, sheet, sheetName);

    const guideRows =
      entityType === 'recipe'
        ? [
            ['RDMS Assigner Recipe Bulk Upload — Template Guide'],
            [''],
            ['Required Columns', 'RecipeName*, Source*, Description*'],
            ['Optional Columns', 'OriginalName (Alias), AlternateName'],
            ['Formats', 'Upload .xlsx, .xls, or .csv files.'],
          ]
        : [
            ['RDMS Assigner Product Bulk Upload — Template Guide'],
            [''],
            ['Required Columns', 'BrandName*, ProductName*'],
            ['Optional Columns', 'DataSource, DatabaseSource, CountryOfOrigin (or select in preview)'],
            ['Formats', 'Upload .xlsx, .xls, or .csv files.'],
          ];
    const guideSheet = XLSX.utils.aoa_to_sheet(guideRows);
    guideSheet['!cols'] = [{ wch: 24 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(book, guideSheet, 'Guide');

    XLSX.writeFile(book, `${baseName}.xlsx`);
    return;
  }

  const csvContent = '\uFEFF' + headers.join(',') + '\n';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${baseName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadCsvTemplate(entityType: AssignerBulkEntityType): void {
  downloadAssignerBulkTemplate(entityType, 'csv');
}

/** First sheet of .xlsx/.xls → same shape as parseCsvText. */
export function parseExcelArrayBuffer(buffer: ArrayBuffer): { headers: string[]; rows: Record<string, string>[] } {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) {
    return { headers: [], rows: [] };
  }

  const sheet = workbook.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<(string | number | boolean | null | undefined)[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });

  if (!aoa?.length) {
    return { headers: [], rows: [] };
  }

  const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
  if (!headers.some((h) => h.length > 0)) {
    return { headers: [], rows: [] };
  }

  const rows = aoa
    .slice(1)
    .filter((values) => (values || []).some((cell) => String(cell ?? '').trim().length > 0))
    .map((values) => {
      const record: Record<string, string> = {};
      headers.forEach((header, i) => {
        if (!header) return;
        record[header] = String(values?.[i] ?? '').trim();
      });
      return record;
    });

  return { headers, rows };
}

function findByName(
  options: AssignerBulkLookupOption[] | undefined,
  name: string
): AssignerBulkLookupOption | null {
  if (!name || !options?.length) return null;
  const needle = name.trim().toLowerCase();
  if (!needle) return null;

  const byExact = options.find((o) => String(o.name || '').trim().toLowerCase() === needle);
  if (byExact) return byExact;

  const byId = options.find((o) => String(o.id) === name.trim());
  if (byId) return byId;

  // Allow CSV shorthand like "Green" → "Green Color Code" (prefix / contains).
  // Skip ultra-short needles to avoid false matches (e.g. "g").
  if (needle.length >= 3) {
    const byPrefix = options.find((o) => {
      const n = String(o.name || '').trim().toLowerCase();
      return n.startsWith(needle) || needle.startsWith(n);
    });
    if (byPrefix) return byPrefix;

    const byContains = options.find((o) => {
      const n = String(o.name || '').trim().toLowerCase();
      return n.includes(needle) || needle.includes(n);
    });
    if (byContains) return byContains;
  }

  return null;
}

function buildPreviewRow(
  rowNumber: number,
  cells: Record<string, string>,
  display: Record<string, string>,
  errors: string[],
  selections: AssignerBulkPreviewRow['selections'],
  payload?: Record<string, unknown>
): AssignerBulkPreviewRow {
  const valid = errors.length === 0;
  return {
    rowNumber,
    valid,
    error: valid ? undefined : errors.join('; '),
    cells,
    display,
    selections: selections || {},
    payload: valid ? payload : undefined,
  };
}

export function findLookupById(
  options: AssignerBulkLookupOption[] | undefined,
  id: string | number | null | undefined
): AssignerBulkLookupOption | null {
  if (id == null || id === '' || !options?.length) return null;
  return options.find((o) => String(o.id) === String(id)) || null;
}

export function findLookupByName(
  options: AssignerBulkLookupOption[] | undefined,
  name: string
): AssignerBulkLookupOption | null {
  return findByName(options, name);
}

export function rebuildRecipePayload(
  row: AssignerBulkPreviewRow,
  lookups: AssignerBulkLookupMaps
): void {
  const recipeName = (row.display.RecipeName || '').trim();
  const description = (row.display.Description || '').trim();
  const originalName = (row.display.OriginalName || '').trim();
  const alternateName = (row.display.AlternateName || '').trim();
  const sourceMatch = findLookupById(lookups.sources, row.selections.sourceId);

  const errors: string[] = [];
  if (!recipeName) errors.push('RecipeName is required');
  if (!sourceMatch) errors.push('Source is required');
  if (!description) errors.push('Description is required');

  row.display.Source = sourceMatch?.name || '';
  row.valid = errors.length === 0;
  row.error = row.valid ? undefined : errors.join('; ');

  if (!row.valid) {
    row.payload = undefined;
    return;
  }

  row.payload = {
    name: recipeName,
    originalName: originalName || null,
    alternateName: alternateName || null,
    description,
    status: 'To be assigned',
    isCustom: false,
    source: sourceMatch!.name,
    linkSourceIds:
      sourceMatch?.id != null
        ? [Number(sourceMatch.id)].filter((id) => Number.isFinite(id) && id > 0)
        : [],
    ingredients: [],
    media: [],
    localNames: [],
    cuisineIds: [],
    cuisineContinentIds: [],
    cuisineRegionIds: [],
    cuisineSubRegionIds: [],
    cuisineSubSubRegionIds: [],
    mealTypeIds: [],
    categoryIds: [],
    allergenIds: [],
    allergenFreeIds: [],
    cookingMethodIds: [],
    tasteIds: [],
    flavourIds: [],
    textureIds: [],
    aromaIds: [],
    seasonIds: [],
    occasionIds: [],
    mealPreferenceIds: [],
    dietPreferenceIds: [],
    intoleranceIds: [],
    intoleranceFreeIds: [],
    courseIds: [],
    healthColorCodeIds: [],
    utensilEquipmentIds: [],
    diseaseIds: [],
    foodExchangeIds: [],
    faoFoodGroupIds: [],
    locationIds: [],
    chefMethodProcesses: [],
    chefAuthorNotes: [],
    chefAuthorTips: [],
    professionalNotes: [],
  };
}

export function rebuildProductPayload(
  row: AssignerBulkPreviewRow,
  lookups: AssignerBulkLookupMaps,
  entityType: AssignerBulkEntityType
): void {
  const brandName = (row.display.BrandName || '').trim();
  const productName = (row.display.ProductName || '').trim();
  const dataSourceMatch = findLookupById(lookups.dataSources, row.selections.dataSourceId);
  const databaseSourceMatch = findLookupById(
    lookups.databaseSources,
    row.selections.databaseSourceId
  );
  const countryMatch = findLookupById(lookups.countries, row.selections.countryOfOriginId);

  const errors: string[] = [];
  if (!brandName) errors.push('BrandName is required');
  if (!productName) errors.push('ProductName is required');

  row.display.DataSource = dataSourceMatch?.name || '';
  row.display.DatabaseSource = databaseSourceMatch?.name || '';
  row.display.CountryOfOrigin = countryMatch?.name || '';
  row.valid = errors.length === 0;
  row.error = row.valid ? undefined : errors.join('; ');

  if (!row.valid) {
    row.payload = undefined;
    return;
  }

  const dataSourceId = dataSourceMatch?.id != null ? String(dataSourceMatch.id) : '';
  const databaseSourceId = databaseSourceMatch?.id != null ? String(databaseSourceMatch.id) : '';
  const countryOfOriginId = countryMatch?.id != null ? String(countryMatch.id) : '';

  const payload: Record<string, unknown> = {
    brandName,
    productName,
    status: 'To be Assigned',
    dataSource: dataSourceId,
    dataSourceId,
    dataSourceName: dataSourceMatch?.name || '',
    databaseSource: databaseSourceId,
    databaseSourceId,
    databaseSourceName: databaseSourceMatch?.name || '',
    countryOfOrigin: countryOfOriginId,
    countryOfOriginId,
    countryOfOriginName: countryMatch?.name || '',
  };

  if (entityType === 'supplement') {
    payload.productType = 'Supplement';
  }

  row.payload = payload;
}

export function refreshParseCounts(result: AssignerBulkParseResult): void {
  const validCount = result.rows.filter((r) => r.valid).length;
  result.validCount = validCount;
  result.errorCount = result.rows.length - validCount;
  result.totalCount = result.rows.length;
}

export function validateAssignerBulkRows(
  entityType: AssignerBulkEntityType,
  headers: string[],
  rows: Record<string, string>[],
  lookups: AssignerBulkLookupMaps
): AssignerBulkParseResult {
  if (!headers.length) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: 0,
      fileError: 'File is empty or missing headers.',
    };
  }

  if (rows.length > ASSIGNER_BULK_MAX_ROWS) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: rows.length,
      fileError: `File exceeds the limit of ${ASSIGNER_BULK_MAX_ROWS} rows (${rows.length} found). Please split the file and try again.`,
    };
  }

  if (entityType === 'recipe') {
    return validateRecipeRows(rows, lookups);
  }
  return validateProductRows(rows, lookups, entityType);
}

export function validateAssignerBulkCsv(
  entityType: AssignerBulkEntityType,
  csvText: string,
  lookups: AssignerBulkLookupMaps
): AssignerBulkParseResult {
  const { headers, rows } = parseCsvText(csvText);
  return validateAssignerBulkRows(entityType, headers, rows, lookups);
}

function validateRecipeRows(
  rows: Record<string, string>[],
  lookups: AssignerBulkLookupMaps
): AssignerBulkParseResult {
  const seenNames = new Set<string>();
  const previewRows: AssignerBulkPreviewRow[] = [];

  rows.forEach((raw, index) => {
    const recipeName = cell(raw, 'RecipeName', 'Recipe Name', 'Name');
    const source = cell(raw, 'Source');
    const description = cell(raw, 'Description', 'Recipe Description');
    const originalName = cell(raw, 'OriginalName', 'Original Name', 'Alias');
    const alternateName = cell(raw, 'AlternateName', 'Alternate Name');

    const display = {
      RecipeName: recipeName,
      Source: source,
      Description: description,
      OriginalName: originalName,
      AlternateName: alternateName,
    };

    const errors: string[] = [];
    if (!recipeName) errors.push('RecipeName is required');
    if (!source) errors.push('Source is required');
    if (!description) errors.push('Description is required');

    const nameKey = recipeName.toLowerCase();
    if (recipeName && seenNames.has(nameKey)) {
      errors.push(`Duplicate RecipeName "${recipeName}" in this CSV`);
    } else if (recipeName) {
      seenNames.add(nameKey);
    }

    let sourceMatch: AssignerBulkLookupOption | null = null;
    if (source) {
      sourceMatch = findByName(lookups.sources, source);
      if (!sourceMatch) {
        errors.push(`Unknown Source "${source}" — pick from dropdown`);
      }
    }

    const selections = {
      sourceId: sourceMatch?.id ?? null,
    };

    const payload: Record<string, unknown> = {
      name: recipeName,
      originalName: originalName || null,
      alternateName: alternateName || null,
      description,
      status: 'To be assigned',
      isCustom: false,
      source: sourceMatch?.name || source,
      linkSourceIds: sourceMatch?.id != null ? [Number(sourceMatch.id)].filter((id) => Number.isFinite(id) && id > 0) : [],
      ingredients: [],
      media: [],
      localNames: [],
      cuisineIds: [],
      cuisineContinentIds: [],
      cuisineRegionIds: [],
      cuisineSubRegionIds: [],
      cuisineSubSubRegionIds: [],
      mealTypeIds: [],
      categoryIds: [],
      allergenIds: [],
      allergenFreeIds: [],
      cookingMethodIds: [],
      tasteIds: [],
      flavourIds: [],
      textureIds: [],
      aromaIds: [],
      seasonIds: [],
      occasionIds: [],
      mealPreferenceIds: [],
      dietPreferenceIds: [],
      intoleranceIds: [],
      intoleranceFreeIds: [],
      courseIds: [],
      healthColorCodeIds: [],
      utensilEquipmentIds: [],
      diseaseIds: [],
      foodExchangeIds: [],
      faoFoodGroupIds: [],
      locationIds: [],
      chefMethodProcesses: [],
      chefAuthorNotes: [],
      chefAuthorTips: [],
      professionalNotes: [],
    };

    previewRows.push(buildPreviewRow(index + 1, raw, display, errors, selections, payload));
  });

  return summarize(previewRows);
}

function validateProductRows(
  rows: Record<string, string>[],
  lookups: AssignerBulkLookupMaps,
  entityType: AssignerBulkEntityType
): AssignerBulkParseResult {
  const seenKeys = new Set<string>();
  const previewRows: AssignerBulkPreviewRow[] = [];
  const status = 'To be Assigned';

  rows.forEach((raw, index) => {
    const brandName = cell(raw, 'BrandName', 'Brand Name');
    const productName = cell(raw, 'ProductName', 'Product Name');
    const dataSource = cell(raw, 'DataSource', 'Data Source');
    const databaseSource = cell(raw, 'DatabaseSource', 'Database Source');
    const countryOfOrigin = cell(raw, 'CountryOfOrigin', 'Country Of Origin', 'Country');

    const display = {
      BrandName: brandName,
      ProductName: productName,
      DataSource: dataSource,
      DatabaseSource: databaseSource,
      CountryOfOrigin: countryOfOrigin,
    };

    const errors: string[] = [];
    if (!brandName) errors.push('BrandName is required');
    if (!productName) errors.push('ProductName is required');

    const dupKey = `${brandName.toLowerCase()}||${productName.toLowerCase()}`;
    if (brandName && productName && seenKeys.has(dupKey)) {
      errors.push(`Duplicate BrandName+ProductName "${brandName} / ${productName}" in this CSV`);
    } else if (brandName && productName) {
      seenKeys.add(dupKey);
    }

    let dataSourceMatch: AssignerBulkLookupOption | null = null;
    let databaseSourceMatch: AssignerBulkLookupOption | null = null;
    let countryMatch: AssignerBulkLookupOption | null = null;

    // Optional lookups: resolve when known; unknown CSV values left empty for manual dropdown fill
    if (dataSource) {
      dataSourceMatch = findByName(lookups.dataSources, dataSource);
    }
    if (databaseSource) {
      databaseSourceMatch = findByName(lookups.databaseSources, databaseSource);
    }
    if (countryOfOrigin) {
      countryMatch = findByName(lookups.countries, countryOfOrigin);
    }

    const selections = {
      dataSourceId: dataSourceMatch?.id ?? null,
      databaseSourceId: databaseSourceMatch?.id ?? null,
      countryOfOriginId: countryMatch?.id ?? null,
    };

    // Keep display empty when unresolved so dropdown placeholder shows
    display.DataSource = dataSourceMatch?.name || '';
    display.DatabaseSource = databaseSourceMatch?.name || '';
    display.CountryOfOrigin = countryMatch?.name || '';

    const dataSourceId = dataSourceMatch?.id != null ? String(dataSourceMatch.id) : '';
    const databaseSourceId = databaseSourceMatch?.id != null ? String(databaseSourceMatch.id) : '';
    const countryOfOriginId = countryMatch?.id != null ? String(countryMatch.id) : '';

    const payload: Record<string, unknown> = {
      brandName,
      productName,
      status,
      dataSource: dataSourceId,
      dataSourceId,
      dataSourceName: dataSourceMatch?.name || '',
      databaseSource: databaseSourceId,
      databaseSourceId,
      databaseSourceName: databaseSourceMatch?.name || '',
      countryOfOrigin: countryOfOriginId,
      countryOfOriginId,
      countryOfOriginName: countryMatch?.name || '',
    };

    if (entityType === 'supplement') {
      payload.productType = 'Supplement';
    }

    previewRows.push(buildPreviewRow(index + 1, raw, display, errors, selections, payload));
  });

  return summarize(previewRows);
}

function summarize(rows: AssignerBulkPreviewRow[]): AssignerBulkParseResult {
  const validCount = rows.filter((r) => r.valid).length;
  return {
    rows,
    validCount,
    errorCount: rows.length - validCount,
    totalCount: rows.length,
  };
}

export function extractMetadataOptions(list: any[] | undefined | null): AssignerBulkLookupOption[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => ({
      id: item?.id ?? item?.Id ?? item?.value ?? '',
      name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
    }))
    .filter((o) => o.name);
}
